import { usInventoryHref } from './usInventoryLink';
/**
 * #1226 — Polymarket US rows of the shared Open Orders / Open Positions
 * lists: the catalogue lookups behind their chips, links and sort (one
 * classifier and one serving reader per session), the row builders the
 * Tracker's sorted lists take them from, the Close control and the action
 * props a US row hands the shared `OrderActionButtons`.
 *
 * The rule (Brendan, Oct 16): a third venue under the Tracker's own
 * conventions. Evidence vocabulary (reason codes, "unconfirmed") lives in a
 * `title`, never in a cell; the account is named only where it can differ.
 */
import { useEffect, useMemo, useRef, useSyncExternalStore, type SyntheticEvent } from 'react';
import { Loader2 } from 'lucide-react';
import { sportMatchesFilter, type SportFilter } from '@/lib/kalshiTicker/sportFilter';
import { createUsBrowserMarketClient } from '@/lib/polymarketUs/browserMarketClient';
import { isUsCancelUnconfirmedCode, US_CANCEL_UNCONFIRMED_TEXT } from '@/lib/polymarketUs/cancelOutcome';
import { UsMarketClassifier, type UsLookupAccount } from '@/lib/polymarketUs/marketFamily';
import { UsMarketServing } from '@/lib/polymarketUs/marketServing';
import { usOrderKey, usPositionKey } from '@/lib/polymarketUs/portfolioModel';
import { usOrderReasonSentence } from '@/lib/polymarketUs/reason';
import type { UsOrderRow, UsPositionRow } from '@/lib/polymarketUs/types';
import type { OrderActionButtonsProps } from '@/components/molecules/OrderActionButtons';
import type { UsOrderFacts, UsOrderOperationKind } from '@/components/cards/orderCardModel';
import type { PositionCardVM, UsPositionFacts } from '@/components/cards/positionCardModel';
import { VenueChip } from '@/components/atoms/VenueChip';
import { fmtLastFillAt } from '@/components/cards/cardFormat';
import { usPriceCentsLabel } from '@/lib/polymarketUs/format';
import { GameDetailLink } from '@/components/atoms/GameDetailLink';
import { FamilyPendingMark, InfoTooltip, PinToggle } from '@/components/cards/cardKit';
import type { Sport } from '../ValueDashboard/types';
import { familyMatchesFilter, type TrackerFamilyFilter } from './trackerMarketFamily';
import { usGameDetailHref, usGameStartMs, usGameTeams, usPanelTarget, type UsPanelTarget } from './usGameLink';
import { US_ROW_NO_PANEL_TITLE, usHoldingRowPinKey, usOrderRowPinKey, type UsAccountView, type UsOpenPanel, type UsTrackerRead, type UsUnavailablePanel } from './usTrackerFeed';

// ── Catalogue lookups ───────────────────────────────────────────────────────

export type UsTrackerMarkets = {
  classifier: UsMarketClassifier;
  serving: UsMarketServing;
  /** Bumped when a classification answers (links, chips and sort re-derive). */
  version: number;
  /** Bumped when a serving answer lands (the Close rule re-derives). */
  servingVersion: number;
};

/** One classifier (sport, family, game identity) and one serving reader per
 * session; a new login or org starts over. */
export function useUsTrackerMarkets(sessionKey: string): UsTrackerMarkets {
  const classifier = useMemo(() => {
    void sessionKey;
    return new UsMarketClassifier(createUsBrowserMarketClient());
  }, [sessionKey]);
  const serving = useMemo(() => {
    void sessionKey;
    return new UsMarketServing(createUsBrowserMarketClient());
  }, [sessionKey]);
  useEffect(() => {
    classifier.open();
    return () => classifier.close();
  }, [classifier]);
  useEffect(() => {
    serving.open();
    return () => serving.close();
  }, [serving]);
  const version = useSyncExternalStore(classifier.subscribe, classifier.getVersion);
  const servingVersion = useSyncExternalStore(serving.subscribe, serving.getVersion);
  return useMemo(() => ({ classifier, serving, version, servingVersion }), [classifier, serving, version, servingVersion]);
}

/** Ask the catalogue about every market the pooled read holds (sport and
 * family for the chips, the game for links and sort), and whether each
 * holding's market is served (the Close rule). Keyed on the reads alone: a
 * market tick re-renders the links but never re-asks the catalogue. */
export function useUsMarketRequests(read: UsTrackerRead | null, markets: UsTrackerMarkets): void {
  const { classifier, serving } = markets;
  const accounts = read?.accounts ?? null;
  // The read each account was last asked about: a reader re-reporting (an
  // action, a clock sample) with the same read asks nothing again.
  const asked = useRef(new WeakMap<object, object>());
  const heldRead = useRef<{ serving: UsMarketServing; snapshots: Map<string, object> } | null>(null);
  useEffect(() => {
    if (!accounts) return;
    const snapshots = new Map<string, object>();
    for (const account of accounts) {
      if (!account.context || !account.snapshot) continue;
      snapshots.set(account.accountId, account.snapshot);
      if (asked.current.get(account.snapshot) === classifier) continue;
      asked.current.set(account.snapshot, classifier);
      const lookup: UsLookupAccount = { accountId: account.accountId, scope: account.context.scope };
      const slugs = [...new Set([...account.snapshot.orders, ...account.snapshot.positions].map((r) => r.target.marketSlug))];
      if (slugs.length) classifier.request(slugs, lookup);
    }
    const previous = heldRead.current;
    if (!previous || previous.serving !== serving || previous.snapshots.size !== snapshots.size
      || [...snapshots].some(([id, snapshot]) => previous.snapshots.get(id) !== snapshot)) {
      heldRead.current = { serving, snapshots };
      // The serving reader replaces pending work, so all accounts must share
      // one union. Per-account asks can discard earlier accounts' later pages.
      serving.request(accounts.flatMap(account => account.context && account.snapshot
        ? account.openPositions.map(row => row.target.marketSlug) : []));
    }
  }, [accounts, classifier, serving]);
}

// ── Rows ────────────────────────────────────────────────────────────────────

/** A Polymarket US order in the Tracker's sorted Open Orders list. */
export type UsSortedOrderRow = {
  kind: 'us';
  sport: Sport | null;
  sortStart: number;
  sortTicker: string;
  order: UsOrderRow;
  accountId: string;
  /** Listed only because its market is not classified yet under an active chip. */
  familyPending?: boolean;
};

/** A Polymarket US holding in the Tracker's sorted Open Positions list. */
export type UsSortedPosRow = {
  kind: 'us';
  sport: Sport | null;
  sortStart: number;
  sortTicker: string;
  pos: UsPositionRow;
  accountId: string;
  familyPending: boolean;
};

function verdict(markets: UsTrackerMarkets, slug: string, sport: SportFilter, kind: TrackerFamilyFilter) {
  const cls = markets.classifier.get(slug);
  const s: Sport | null = cls?.sport ?? null;
  const family = cls?.family ?? null;
  return {
    sport: s,
    visible: (s === null || sportMatchesFilter(s, sport)) && familyMatchesFilter(family, kind),
    pending: (s === null && sport.size > 0) || (family === null && kind.size > 0),
  };
}

/** The event start the Tracker sorts a US market by: the catalogue's game
 * matched to exactly one board row (`usGameStartMs`); unknown sorts last. */
export function usSortStart(markets: UsTrackerMarkets, slug: string): number {
  return usGameStartMs(markets.classifier.game(slug)) ?? Number.POSITIVE_INFINITY;
}

/** One account's working orders as rows under the Open Orders chips. */
export function usOrderRowsOf(view: UsAccountView, markets: UsTrackerMarkets, sport: SportFilter, kind: TrackerFamilyFilter): UsSortedOrderRow[] {
  const rows: UsSortedOrderRow[] = [];
  for (const order of view.workingOrders) {
    const v = verdict(markets, order.target.marketSlug, sport, kind);
    if (!v.visible) continue;
    rows.push({ kind: 'us', sport: v.sport, sortStart: usSortStart(markets, order.target.marketSlug), sortTicker: order.target.marketSlug,
      order, accountId: view.accountId, ...(v.pending ? { familyPending: true } : {}) });
  }
  return rows;
}

/** One account's open holdings as rows under the Open Positions chips. */
export function usHoldingRowsOf(view: UsAccountView, markets: UsTrackerMarkets, sport: SportFilter, kind: TrackerFamilyFilter): UsSortedPosRow[] {
  const rows: UsSortedPosRow[] = [];
  for (const pos of view.openPositions) {
    const v = verdict(markets, pos.target.marketSlug, sport, kind);
    if (!v.visible) continue;
    rows.push({ kind: 'us', sport: v.sport, sortStart: usSortStart(markets, pos.target.marketSlug), sortTicker: pos.target.marketSlug,
      pos, accountId: view.accountId, familyPending: v.pending });
  }
  return rows;
}

/** #1226 — a US pin's origin, which the stored entry carries (`PinnedEntry.us`,
 * validated on load); nothing for a K/P row. */
export const usPinOriginOf = (us: { accountId: string } | null | undefined) =>
  us ? { us: { accountId: us.accountId, view: 'own' as const } } : {};

/** The row's pin key, which is also its view-model key. */
export const usOrderRowKey = (row: UsSortedOrderRow) => usOrderRowPinKey(row.accountId, row.order);
export const usHoldingRowKey = (row: UsSortedPosRow) => usHoldingRowPinKey(row.accountId, row.pos);

/** What the Tracker's Cancel visible freezes for one account: the US orders
 * shown under the chips, with the ones shown only because their market is
 * not classified yet (`usVisibleInventory`'s shape). */
export function usShownOrdersOf(rows: readonly UsSortedOrderRow[], views: ReadonlyMap<string, UsAccountView>) {
  const byAccount = new Map<string, { accountId: string; scope: UsOrderRow['scope']; readiness: NonNullable<UsAccountView['context']>['readiness'];
    orders: UsOrderRow[]; pending: Set<string> }>();
  for (const row of rows) {
    const view = views.get(row.accountId);
    if (!view?.context) continue;
    let entry = byAccount.get(row.accountId);
    if (!entry) {
      entry = { accountId: row.accountId, scope: view.context.scope, readiness: view.context.readiness, orders: [], pending: new Set() };
      byAccount.set(row.accountId, entry);
    }
    entry.orders.push(row.order);
    if (row.familyPending) entry.pending.add(row.order.intentId);
  }
  return [...byAccount.values()];
}

// ── Links and the docked panel ──────────────────────────────────────────────

/** A US row's game link (the graph chip) and event start, from the catalogue's
 * game matched to the board; unmatched inventory links to its exact Tracker details. */
export function usRowLink(markets: UsTrackerMarkets, target: { marketSlug: string; outcome: 'yes' | 'no' }, accountId: string) {
  const game = markets.classifier.game(target.marketSlug);
  return { href: usGameDetailHref(game, target.outcome, accountId) ?? usInventoryHref({ ...target, accountId }), startMs: usGameStartMs(game), teams: usGameTeams(game),
    opens: !!usPanelTarget(game, target, accountId, 0) };
}

/** The docked panel a US row click opens (resolved again at the click, from
 * the board as it is now), or null when the US tab cannot open on it. */
export function usRowPanel(markets: UsTrackerMarkets, target: { marketSlug: string; outcome: 'yes' | 'no' }, accountId: string,
  interactionSequence: number): { panel: UsPanelTarget | null; href: string | null } {
  const game = markets.classifier.game(target.marketSlug);
  return { panel: usPanelTarget(game, target, accountId, interactionSequence), href: usGameDetailHref(game, target.outcome, accountId) ?? usInventoryHref({ ...target, accountId }) };
}

/** Hover text on a row that opens the panel. */
export const US_ROW_OPEN_TITLE = 'Open the order panel on this account';
export const US_ROW_INVENTORY_TITLE = 'Open saved orders and holdings for this outcome; close the previous trade panel';
export const usRowOpenTitle = (opens: boolean, href: string | null) => opens ? US_ROW_OPEN_TITLE
  : href?.startsWith('/positions?') ? US_ROW_INVENTORY_TITLE : US_ROW_NO_PANEL_TITLE;

// ── Actions ─────────────────────────────────────────────────────────────────

/** The stable handlers a US row calls; the Tracker resolves the account
 * reader and the exact row from the facts. */
export type UsRowDispatch = {
  run(facts: Pick<UsOrderFacts, 'accountId' | 'intentId'>, kind: UsOrderOperationKind): void;
  edit(facts: Pick<UsOrderFacts, 'accountId' | 'intentId'>): void;
  close(facts: Pick<UsPositionFacts, 'accountId' | 'marketSlug' | 'outcome'>): void;
  /** A row click: the Tracker's order panel on the row's US tab and account. */
  open(target: { accountId: string; marketSlug: string; outcome: 'yes' | 'no' }): void;
};


/** #1226 — the dispatch a surface without Polymarket US rows passes nothing
 *  for (tests, previews); stable, so the memo compares equal. */
export const NO_US_DISPATCH: UsRowDispatch = { run: () => {}, edit: () => {}, close: () => {}, open: () => {} };

/** The stable handlers every US row calls. Each resolves the row's account
 * reader and the exact row at the click, from the read on screen, so a
 * memoized row never holds a stale handler. A row click is a new request
 * each time (it re-applies its account over a pick in the panel), resolved
 * from the board as it is now. */
export function useUsRowDispatch(read: UsTrackerRead | null, markets: UsTrackerMarkets,
  openPanel: UsOpenPanel, unavailable: UsUnavailablePanel): UsRowDispatch {
  const readRef = useRef(read);
  const marketsRef = useRef(markets);
  useEffect(() => {
    readRef.current = read;
    marketsRef.current = markets;
  }, [read, markets]);
  const clicks = useRef(0);
  return useMemo<UsRowDispatch>(() => {
    const viewOf = (accountId: string) => readRef.current?.accounts.find((a) => a.accountId === accountId) ?? null;
    const orderOf = (accountId: string, intentId: string) => {
      const view = viewOf(accountId);
      const row = view?.snapshot?.orders.find((o) => o.intentId === intentId) ?? null;
      return view && row ? { view, row } : null;
    };
    return {
      run: ({ accountId, intentId }, kind) => {
        const hit = orderOf(accountId, intentId);
        if (hit) hit.view.actions.run(kind, hit.row);
      },
      edit: ({ accountId, intentId }) => {
        const hit = orderOf(accountId, intentId);
        if (hit) hit.view.actions.edit(hit.row);
      },
      close: ({ accountId, marketSlug, outcome }) => {
        const view = viewOf(accountId);
        const pos = view?.openPositions.find((p) => p.target.marketSlug === marketSlug && p.target.outcome === outcome) ?? null;
        const state = view && pos ? view.closeStates.get(usPositionKey(pos)) ?? null : null;
        if (view && pos && state && 'quantity' in state) view.actions.close(pos, state.quantity);
      },
      open: ({ accountId, marketSlug, outcome }) => {
        const { panel, href } = usRowPanel(marketsRef.current, { marketSlug, outcome }, accountId, ++clicks.current);
        if (panel) openPanel({ ...panel, marketSlug, href });
        else unavailable({ marketSlug, href });
      },
    };
  }, [openPanel, unavailable]);
}

const WAITING_TITLE = 'Waiting for another action on this account to finish.';
const CHECK_STATUS_TITLE = 'Check status: reads the order; never cancels or resends.';
/** Whole seconds until a repeat cancel unlocks, rounded up so "in 1s" shows until it is really available. */
export const repeatSecondsLeft = (availableAtMs: number, nowMs: number) => Math.max(0, Math.ceil((availableAtMs - nowMs) / 1000));

/**
 * A US order's action set in the K/P icon style: Cancel (or, after an
 * unconfirmed cancel, a repeat cancel: disabled with the countdown in its
 * title until the quiet interval passes), Edit (greyed with its reason when
 * it can't be used), and Check status. The existing amber unconfirmed state
 * carries the US cancel status. Null for a finished order, which shows its
 * word and no control. Every rule comes from `portfolioModel.usCancelView`,
 * resolved into the facts by `buildUsOrderVM`.
 */
export function usOrderActionProps(facts: UsOrderFacts, dispatch: UsRowDispatch): OrderActionButtonsProps | null {
  if (facts.terminal) return null;
  const run = (kind: UsOrderOperationKind) => dispatch.run(facts, kind);
  const mine = facts.pendingKind;
  const waiting = facts.busy && mine === null;
  const sent = facts.cancelStatus !== null;
  const error = facts.error;
  const errorText = error ? (isUsCancelUnconfirmedCode(error) ? US_CANCEL_UNCONFIRMED_TEXT : `Last action failed: ${usOrderReasonSentence(error)}`) : null;
  const errorTitle = error ? `${errorText} [${error}]` : null;
  // The repeat wait: a sent attempt whose repeat is not yet available.
  const left = facts.repeatAvailableAtMs !== null && !facts.canRepeatCancel ? repeatSecondsLeft(facts.repeatAvailableAtMs, facts.nowMs) : null;
  let cancelTitle: string;
  let cancelDisabled: boolean;
  let onCancel: () => void;
  if (facts.canCancel) {
    cancelTitle = errorTitle ?? (waiting ? WAITING_TITLE : 'Cancel order');
    cancelDisabled = waiting;
    onCancel = () => run('cancel');
  } else if (sent) {
    const status = facts.cancelStatus!;
    cancelTitle = [errorTitle ?? status,
      facts.canRepeatCancel ? 'Click to send another cancel for this order.'
        : left !== null ? `Repeat cancel in ${left}s (the first cancel is given 10 s to settle).` : null,
    ].filter(Boolean).join(' ');
    cancelDisabled = waiting || !facts.canRepeatCancel;
    onCancel = () => run('repeat');
  } else {
    cancelTitle = errorTitle ?? facts.blockedReason ?? 'Cancel unavailable';
    cancelDisabled = true;
    onCancel = () => {};
  }
  return {
    // Another row's action on this account holds every button; the titles say so.
    disabled: waiting,
    onCancel,
    onEdit: () => dispatch.edit(facts),
    onCheckStatus: facts.canCheckStatus ? () => run('status') : undefined,
    canceling: mine === 'cancel' || mine === 'repeat',
    checkingStatus: mine === 'status',
    unconfirmed: (sent && !facts.canCancel) || (!!error && isUsCancelUnconfirmedCode(error)),
    cancelTitle,
    cancelDisabled,
    editAvailable: facts.editOffered,
    editDisabled: facts.editOffered && (waiting || facts.editReason !== null),
    editTitle: facts.editReason ?? (waiting ? WAITING_TITLE : 'Edit order'),
    checkStatusTitle: waiting ? WAITING_TITLE : errorTitle ? `${CHECK_STATUS_TITLE} ${errorTitle}` : CHECK_STATUS_TITLE,
    checkStatusMessage: errorText ?? undefined,
    checkStatusFailed: facts.protocol === 'institutional' && !!error,
  };
}

const stop = (e: SyntheticEvent) => e.stopPropagation();
const CLOSE_BUTTON =
  'rounded border border-red-500/40 px-2 py-0.5 text-[10px] font-medium text-red-400 light:text-red-700 hover:bg-red-500/10 disabled:opacity-50';

/**
 * A US holding's Close, with `PolyCloseControl`'s visual contract: the button
 * for the reader's size, or the same button greyed with the reason in its
 * title; nothing when the reader shows no Close for this holding (a known
 * zero or short holding). A click asks the Tracker to open the US Close
 * window; nothing is sent here.
 */
export function UsCloseControl({ facts, onClose }: { facts: UsPositionFacts; onClose: () => void }) {
  if (facts.closeQuantity === null && facts.closeReason === null) return null;
  const ready = facts.closeQuantity !== null && !facts.busy;
  const title = facts.closeReason
    ?? (facts.busy ? 'Close waits while this account’s holdings refresh or another action on them finishes.'
      // #1113: nothing rests only with an immediate duration, which the
      // preset uses when the market offers one; say to check it.
      : `Opens a sell of ${facts.closeQuantity} in the US ticket at the lowest price. With immediate-or-cancel or fill-or-kill nothing rests; check the duration before Place Order.`);
  return (
    // The cards open the order panel on click; Close is its own action, and
    // a click on the greyed button must not open the panel either.
    <span className="inline-flex shrink-0 items-center gap-1" onClick={stop} title={title}>
      <button type="button" disabled={!ready} onClick={onClose} title={title}
        data-testid={ready || facts.busy ? 'us-position-close' : 'us-position-close-unavailable'}
        aria-label={facts.closeQuantity !== null ? `Close ${facts.marketSlug} ${facts.outcome.toUpperCase()}: sell ${facts.closeQuantity}` : 'Close unavailable'}
        className={CLOSE_BUTTON}>
        {facts.busy && facts.closeQuantity !== null ? <Loader2 size={12} className="inline animate-spin" /> : 'Close'}
      </button>
      {!ready && <InfoTooltip title="Why Close is unavailable"><p>{title}</p></InfoTooltip>}
    </span>
  );
}

/** The small muted account word beside a US row's venue chip, only where it
 * can differ (two or more own-view accounts); nothing with one. The account
 * is provenance, not a column that grows with the longest name: cards show
 * the name within a fixed width, the tables' Source cell (`compact`) its
 * number alone (`#2`), and the full name and id are the hover either way. */
export function UsAccountWord({ label, accountId, className = '', compact = false }: {
  label: string | null; accountId: string; className?: string; compact?: boolean;
}) {
  if (!label) return null;
  const number = compact ? /^Account (\d+)\b/.exec(label)?.[1] : undefined;
  return (
    <span className={`text-muted-foreground inline-block max-w-[6rem] truncate whitespace-nowrap align-bottom text-[10px] ${className}`}
      title={`${label} · account ${accountId}`} data-testid="us-row-account">
      {number ? `#${number}` : label}
    </span>
  );
}

/** A US order row's one-word mark, in the K/P pill style: amber says the
 * order needs the trader's eye, muted a finished state. Null when resting. */
export function usStatusPillClass(tone: 'amber' | 'muted'): string {
  return tone === 'amber' ? 'bg-amber-400/20 text-amber-300 light:text-amber-700' : 'bg-muted text-muted-foreground';
}

/** `usOrderKey` for a sorted US row (the reader's key for pending and errors). */
export const usReaderKeyOf = (row: UsSortedOrderRow) => usOrderKey(row.order);

// ── The Open Positions table row ────────────────────────────────────────────

/**
 * A Polymarket US holding in the shared Open Positions table: the same
 * thirteen columns in the K/P row height. Qty is G1's exact decimal size
 * (with the partly-known word beside it); economics come from the shared VM
 * and unavailable inputs stay dashed. Pin and
 * Close ride the Cash Out cell, where the K/P rows keep theirs.
 */
export function UsPositionTableRow({ row, vm, viewAnchor, pinned, href, opens, togglePin, usDispatch }: {
  row: UsSortedPosRow;
  vm: PositionCardVM;
  viewAnchor: string;
  pinned: boolean;
  /** The row's game link (`usRowLink`), or null. */
  href: string | null;
  /** Whether a click opens the docked panel (the hover says so, as the orders table's rows do). */
  opens: boolean;
  togglePin: (entry: { key: string; kind: 'position'; label: string; us?: { accountId: string; view: 'own' | 'desk' } }) => void;
  usDispatch: UsRowDispatch;
}) {
  const facts = vm.us!;
  const label = facts.marketLabel;
  const closeShown = facts.closeQuantity !== null || facts.closeReason !== null;
  const money = (value: number | null) => value === null ? '—' : value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  const cents = (value: number | null) => value === null ? '—' : `${Number(value.toFixed(2))}¢`;
  const metric = (text: string) => <td className="text-foreground px-3 py-2 text-right font-mono text-xs" title={facts.economicsTitle}>{text}</td>;
  return (
    <tr
      data-view-anchor={viewAnchor}
      data-testid="us-position-card"
      data-position-key={vm.key}
      className="border-border/40 hover:bg-muted/30 cursor-pointer border-b"
      title={usRowOpenTitle(opens, href)}
      onClick={() => usDispatch.open({ accountId: facts.accountId, marketSlug: facts.marketSlug, outcome: facts.outcome })}
    >
      <td className="text-foreground px-4 py-2 font-mono text-xs">
        <span title={row.pos.target.marketSlug}>{label}</span>
        {row.familyPending && <FamilyPendingMark />}
        {href && <GameDetailLink href={href} className="-my-1 ml-1" />}
      </td>
      <td className="px-2 py-2 text-center">
        <span className="inline-flex items-center gap-1">
          <VenueChip venue="polymarket_us" />
          <UsAccountWord label={facts.accountLabel} accountId={facts.accountId} compact />
        </span>
      </td>
      <td className="px-3 py-2 text-center">
        <span
          className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${
            row.pos.target.outcome === 'yes' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
          }`}
        >
          {row.pos.target.outcome.toUpperCase()}
        </span>
      </td>
      <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
        {facts.partialText && (
          <span className="mr-1.5 font-sans text-[10px] font-medium text-amber-400 light:text-amber-700" title={facts.partialTitle ?? undefined}
            data-testid="us-position-partial">
            {facts.partialText}
          </span>
        )}
        <span data-testid="us-position-held">{vm.qtyText}</span>
      </td>
      {metric(cents(vm.avgCents))}
      <td className="text-muted-foreground px-3 py-2 text-right font-mono text-xs"
        title={row.pos.lastFill ? `${row.pos.lastFill.at}${row.pos.lastFill.priceUsd === null ? '; price unknown'
          : `; ${usPriceCentsLabel(row.pos.lastFill.priceUsd)} cleared fill`}` : undefined}>
        {fmtLastFillAt(vm.lastFillAt)}
      </td>
      <td className="text-muted-foreground px-3 py-2 text-right font-mono text-xs" title={facts.feesTitle}>
        <span data-testid="us-position-fees">{vm.feesText}</span>
      </td>
      {metric(money(vm.costDollars))}
      {metric(cents(vm.nowCents))}
      <td className="text-muted-foreground px-3 py-2 text-right font-mono text-xs" title="Polymarket mid unavailable for US positions">—</td>
      <td className="border-border/40 border-l px-3 py-2 text-right font-mono text-xs font-medium" title={facts.economicsTitle}>{money(vm.pnlDollars)}</td>
      <td className="border-border/40 border-r px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-end gap-2">
          {(vm.cashOutDollars !== null || !closeShown) && <span className="font-mono text-xs" title={facts.economicsTitle}>{money(vm.cashOutDollars)}</span>}
          <PinToggle pinned={pinned} onToggle={() => togglePin({ key: vm.key, kind: 'position', label, ...usPinOriginOf(facts) })} />
          <UsCloseControl facts={facts} onClose={() => usDispatch.close(facts)} />
        </div>
      </td>
      {metric(money(vm.ifWinDollars))}
    </tr>
  );
}
